import React, { useState, useRef, useEffect } from 'react';
import AdminPanel from '../panel/AdminPanel';
import '../styles/ComposeNotification.css';
import api from "../../../api/api";

const ComposeNotificationDashboard = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const fileInputRef = useRef(null);

  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const [formData, setFormData] = useState({
    targetAudience: 'All Users',
    selectedRecipients: [],
    selectAll: false,
    title: '',
    message: '',
    attachment: null,
  });

  // Fetch students and teachers
  useEffect(() => {
    api.get("/api/students")
      .then(res => setStudents(res.data.map(s => ({ id: s.student_id ?? s.id, name: s.name }))))
      .catch(err => console.error(err));

    api.get("/api/teachers")
      .then(res => setTeachers(res.data.map(t => ({ id: t.teacher_id ?? t.id, name: t.name }))))
      .catch(err => console.error(err));
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle recipient checkbox
  const handleRecipientChange = (id) => {
    setFormData(prev => {
      const updatedRecipients = prev.selectedRecipients.includes(id)
        ? prev.selectedRecipients.filter(r => r !== id)
        : [...prev.selectedRecipients, id];
      return { ...prev, selectedRecipients: updatedRecipients, selectAll: false };
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    let allIds = [];
    if (formData.targetAudience === 'Students') allIds = students.map(s => s.id);
    if (formData.targetAudience === 'Teachers') allIds = teachers.map(t => t.id);

    setFormData(prev => ({
      ...prev,
      selectedRecipients: prev.selectAll ? [] : allIds,
      selectAll: !prev.selectAll,
    }));
  };

  // Submit notification
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.message.trim()) {
      alert("Please fill all required fields!");
      return;
    }

    try {
      const payload = new FormData();
      payload.append("title", formData.title);
      payload.append("message", formData.message);
      payload.append("targetAudience", formData.targetAudience);
      payload.append("selectedRecipients", JSON.stringify(formData.selectedRecipients));
      if (formData.attachment) payload.append("attachment", formData.attachment);

const res = await api.post("/api/notifications", payload);

const data = res.data;

if (res.status !== 200) {
  throw new Error(data.error || "Failed to save notification");
}

      alert(`✅ Notification Sent Successfully! ID: ${data.notificationId}`);

      // Reset form
      setFormData({
        targetAudience: "All Users",
        selectedRecipients: [],
        selectAll: false,
        title: "",
        message: "",
        attachment: null,
      });
      if (fileInputRef.current) fileInputRef.current.value = null;

    } catch (err) {
      console.error(err);
      alert("❌ Error sending notification: " + err.message);
    }
  };

  // Recipient selection UI
  const renderRecipientSelector = () => {
    let options = [];
    let label = '';

    if (formData.targetAudience === 'Students') {
      options = students;
      label = 'Students';
    } else if (formData.targetAudience === 'Teachers') {
      options = teachers;
      label = 'Teachers';
    }

    if (!options.length) return null;

    return (
      <div className="recipient-selector">
        <strong>{label}</strong>
        <table className="recipient-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.selectAll}
                    onChange={handleSelectAll}
                  /> Select All
                </label>
              </th>
            </tr>
          </thead>
          <tbody>
            {options.map((item, index) => (
              <tr key={`${formData.targetAudience}-${item.id ?? item.name}-${index}`}>
                <td>{item.name} ({item.id ?? 'N/A'})</td>
                <td>
                  <input
                    type="checkbox"
                    checked={formData.selectedRecipients.includes(item.id)}
                    onChange={() => handleRecipientChange(item.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <AdminPanel onToggle={setIsSidebarCollapsed} />

      <div className={`notification-main-content ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <h1 style={{ margin: '20px 0' }}>Push Notification Composer</h1>

        <form className="notification-form-full" onSubmit={handleSubmit}>
          <label>
            Target Audience:
            <select
              name="targetAudience"
              value={formData.targetAudience}
              onChange={handleChange}
            >
              <option value="All Users">All Users</option>
              <option value="Students">Students</option>
              <option value="Teachers">Teachers</option>
            </select>
          </label>

          {formData.targetAudience !== 'All Users' && renderRecipientSelector()}

          <label>
            Notification Title:
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter notification title"
              required
            />
          </label>

          <label>
            Notification Message:
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Enter your message"
              rows={6}
              required
            />
          </label>

          <label>
            Attach File:
            <input type="file" name="attachment" ref={fileInputRef} onChange={handleChange} />
          </label>

          <button type="submit" className="send-btn">Send Notification</button>
        </form>
      </div>
    </div>
  );
};

export default ComposeNotificationDashboard;
